import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function Precos() {
  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-6xl py-16 sm:py-24">
        <h1 className="font-serif text-display-lg text-balance">Preços simples e transparentes</h1>
        <p className="mt-4 text-lg text-muted-foreground text-pretty max-w-2xl">
          Você não paga assinatura. Cada advogado define o próprio preço. A plataforma cobra 15% de fee por cima — embutido no valor que você vê.
        </p>

        <div className="mt-16 grid gap-6 md:grid-cols-2">
          <Card className="border-border/60">
            <CardHeader>
              <Badge variant="muted" className="w-fit mb-2">Para usuários</Badge>
              <CardTitle>Você paga só pelo que usar</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-serif text-4xl">
                R$ 0<span className="text-base text-muted-foreground font-sans"> / mês</span>
              </p>
              <p className="text-muted-foreground text-sm mt-1">Sem mensalidade, sem cobrança recorrente.</p>
              <ul className="mt-6 space-y-3 text-sm">
                <Feature text="Pré-análise IA gratuita do contrato (até 3/mês)" />
                <Feature text="Marketplace completo de advogados" />
                <Feature text="Chat in-app e videocall via Daily.co" />
                <Feature text="Pagamento via PIX ou cartão com escrow" />
                <Feature text="Histórico de versões e download a qualquer momento" />
                <Feature text="Suporte por email" />
              </ul>
              <Button asChild size="lg" variant="accent" className="w-full mt-8">
                <Link to="/cadastro">Começar grátis</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-accent/40 bg-surface relative">
            <div className="absolute -top-3 left-6">
              <Badge variant="accent">Para advogados</Badge>
            </div>
            <CardHeader>
              <CardTitle>Receba clientes pré-qualificados</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-serif text-4xl">
                15%<span className="text-base text-muted-foreground font-sans"> de fee</span>
              </p>
              <p className="text-muted-foreground text-sm mt-1">Embutido no preço que o cliente vê. Você recebe 85%.</p>
              <ul className="mt-6 space-y-3 text-sm">
                <Feature text="Sem mensalidade. Cobrança só por tarefa fechada." />
                <Feature text="Você define sua própria tabela de preços" />
                <Feature text="Cliente já vê a pré-análise IA — você ganha tempo" />
                <Feature text="Escrow garante pagamento ao entregar" />
                <Feature text="Avaliações públicas viram seu portfólio" />
                <Feature text="Saque via PIX (1 dia útil após aprovação)" />
              </ul>
              <Button asChild size="lg" variant="default" className="w-full mt-8">
                <Link to="/cadastro/advogado">Cadastrar como advogado</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="mt-20">
          <h2 className="font-serif text-3xl mb-2">Faixas típicas (referência de mercado)</h2>
          <p className="text-muted-foreground mb-8 text-pretty max-w-2xl">
            Cada advogado define livremente. Estes são valores médios praticados na plataforma.
          </p>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-border/60 text-muted-foreground">
                  <tr>
                    <th className="text-left p-4 font-medium">Tipo de tarefa</th>
                    <th className="text-right p-4 font-medium">Faixa típica</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {[
                    ['Revisão de contrato simples', 'R$ 250 – R$ 800'],
                    ['Revisão de contrato complexo', 'R$ 800 – R$ 3.000'],
                    ['Redação de contrato do zero', 'R$ 1.500 – R$ 8.000'],
                    ['Parecer jurídico escrito', 'R$ 600 – R$ 2.500'],
                    ['Consulta por call (1h)', 'R$ 300 – R$ 1.000'],
                    ['Chat consultivo (10 mensagens / 7 dias)', 'R$ 200 – R$ 500'],
                  ].map(([label, range]) => (
                    <tr key={label}>
                      <td className="p-4">{label}</td>
                      <td className="p-4 text-right text-muted-foreground">{range}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        <div className="mt-20">
          <h2 className="font-serif text-3xl mb-8">Perguntas frequentes</h2>
          <dl className="space-y-6">
            {[
              ['Tem mensalidade?', 'Não. Para usuários e advogados, é zero. A plataforma só cobra 15% por cima do preço quando uma tarefa é fechada.'],
              ['Como funciona o escrow?', 'Quando você contrata um advogado, o pagamento é processado via Pagar.me (PIX, cartão ou boleto). O valor fica retido até você aprovar a entrega. Aprovou? Liberado direto pro advogado, com split 85/15. Não aprovou em 7 dias úteis sem motivo? Liberado automaticamente. Disputa? Time da plataforma intermedia.'],
              ['Quem define os preços?', 'Cada advogado individualmente, pra cada tipo de tarefa. A plataforma não tem ingerência sobre valores.'],
              ['Posso recusar uma revisão?', 'Sim. O advogado pode recusar uma tarefa ofertada. O usuário também pode cancelar antes do início da revisão (estorno integral).'],
              ['Posso baixar meu contrato a qualquer momento?', 'Sempre. Original e todas as versões revisadas ficam acessíveis no seu dashboard.'],
              ['Os dados são seguros?', 'Contratos são criptografados em repouso (S3 + KMS) e em trânsito (TLS). Acesso restrito a você e ao advogado contratado. Backup com retenção de 2 anos. Conformidade LGPD.'],
            ].map(([q, a]) => (
              <div key={q} className="border-b border-border/40 pb-6">
                <dt className="font-medium font-serif text-lg">{q}</dt>
                <dd className="mt-2 text-muted-foreground text-pretty">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </MarketingLayout>
  );
}

function Feature({ text, exclude }: { text: string; exclude?: boolean }) {
  return (
    <li className="flex items-start gap-2">
      {exclude ? (
        <X className="h-4 w-4 text-muted-foreground flex-none mt-0.5" />
      ) : (
        <Check className="h-4 w-4 text-success flex-none mt-0.5" />
      )}
      <span className={exclude ? 'text-muted-foreground' : ''}>{text}</span>
    </li>
  );
}
